import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { getProfile, updateProfile, verifyPAN, updateEmployment, uploadDocument, uploadAadhaar, getAadhaarStatus, initiatePayment, verifyPayment, getSubscription } from "@/api";
import { toast } from "sonner";
import { ArrowLeft, User, Briefcase, FileText, Shield, CreditCard } from "lucide-react";
import { openRazorpayCheckout, loadRazorpayScript } from "@/utils/razorpay";
import { markPaymentPending } from "@/components/ActivationBanner";

// Step components
import PersonalAndPANStep from "@/components/onboarding/PersonalAndPANStep";
import EmploymentStep from "@/components/onboarding/EmploymentStep";
import DocumentsStep from "@/components/onboarding/DocumentsStep";
import AadhaarStep from "@/components/onboarding/AadhaarStep";
import AadhaarDigiLockerStep from "@/components/onboarding/AadhaarDigiLockerStep";
import LoanDetailsStep from "@/components/onboarding/LoanDetailsStep";
import PaymentStep from "@/components/onboarding/PaymentStep";

// NOTE: Personal Details + PAN Verification were MERGED into a single
// step on 2026-07 to prevent name/DOB mismatch errors between the two
// previously-separate screens. See PersonalAndPANStep.js.
const STEPS = [
  { key: "personal_and_pan", label: "Personal & PAN", icon: User },
  { key: "employment", label: "Employment", icon: Briefcase },
  { key: "documents", label: "Documents", icon: FileText },
  { key: "aadhaar", label: "Aadhaar KYC", icon: Shield },
  { key: "loan_details", label: "Loan Details", icon: Briefcase },
  { key: "payment", label: "Subscription", icon: CreditCard },
];

export default function CustomerOnboarding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState({});
  const [form, setForm] = useState({ 
    name: "", dob: "", gender: "", pan_number: "", 
    employment_type: "salaried", monthly_income: "",
    loan_type: "personal_loan", lender_name: "",
    emi_start_date: "", emi_end_date: "", loan_amount: "", emi_amount: ""
  });
  const [files, setFiles] = useState({ salary_slip: null, bank_statement: null });
  const [aadhaarStatus, setAadhaarStatus] = useState({ uploaded: false, verified: false, rejection_reason: null });
  // Aadhaar verification mode: 'digilocker' (default) | 'manual' (fallback)
  const [aadhaarMode, setAadhaarMode] = useState('digilocker');
  // Field-level errors surfaced from backend (e.g. PAN name/DOB mismatch)
  const [panFieldErrors, setPanFieldErrors] = useState({});

  useEffect(() => { loadProfile(); }, []);

  // Preload the Razorpay checkout script so it's ready when the user clicks "Pay"
  useEffect(() => {
    loadRazorpayScript().catch(() => {});
  }, []);

  // When the user returns from Digio's DigiLocker flow, our backend redirects
  // them to `/customer/onboarding?digio_status=success` (or `?digio_status=failed`).
  // On mount, detect this signal — OR the localStorage `digio_started` flag
  // (set by AadhaarDigiLockerStep before it redirects out to Digio) — and
  // force the Aadhaar step (index 3) so the child component can immediately
  // surface its result screen and trigger /sync-digio-status.
  // We do NOT strip the query param here — the child component reads it too.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const digioStatus = params.get('digio_status');
      let digioStartedFlag = false;
      try { digioStartedFlag = localStorage.getItem('digio_started') === 'true'; } catch (_) { /* non-fatal */ }
      if (digioStatus === 'success' || digioStatus === 'failed' || digioStartedFlag) {
        setCurrentStep(3);
      }
    } catch (_) { /* non-fatal */ }
  }, []);

  const loadProfile = async () => {
    try {
      const res = await getProfile();
      const p = res.data.profile;
      setProfile(p);
      setForm(prev => ({
        ...prev, name: p.name || "", dob: p.dob || "", gender: p.gender || "", pan_number: p.pan_number || "",
        employment_type: p.employment_type || "salaried", monthly_income: p.monthly_income || "",
      }));
      // Resume to the furthest unfinished step. Step 0 = merged Personal+PAN
      // is considered done only when BOTH personal details AND pan_verified are set.
      if (p.name && p.dob && p.pan_verified) {
        if (p.employment_type && p.monthly_income) {
          if (p.aadhaar_verified) {
            if (p.loan_type && p.lender_name) { setCurrentStep(5); }   // payment
            else { setCurrentStep(4); }                                // loan_details
          } else { setCurrentStep(3); }                                // aadhaar
        } else { setCurrentStep(1); }                                  // employment
      }
    } catch {}
  };

  // Step handlers
  // Merged Personal Details + PAN verification handler.
  // 1. Saves name/dob/gender/pan to profile (so server can read dob for PAN call)
  // 2. Calls /verify-pan (Digio) which reads dob from DB
  // 3. On Digio name/dob mismatch, surfaces field-level errors instead of advancing
  const handlePersonalAndPAN = async () => {
    const pan = (form.pan_number || "").toUpperCase();
    setPanFieldErrors({});
    setLoading(true);
    try {
      // Step 1: persist personal details first so verify-pan can read dob from DB
      await updateProfile({
        name: form.name,
        dob: form.dob,
        gender: form.gender,
        pan_number: pan,
      });

      // Step 2: verify PAN against Digio
      const res = await verifyPAN(pan, form.name);
      const data = res.data || {};

      if (data.verified === true || data.pan_verified === true) {
        toast.success("PAN verified — proceeding to next step");
        setCurrentStep(1);  // → Employment
      } else {
        const msg = (data.message || data.error || "PAN verification failed. Please check your details.").toString();
        const lower = msg.toLowerCase();
        if (lower.includes("name")) {
          setPanFieldErrors({
            name: "Name does not match PAN records. Enter name exactly as on PAN card.",
            pan_number: "Verification failed — check name and date of birth",
          });
        } else if (lower.includes("dob") || lower.includes("date of birth") || lower.includes("date")) {
          setPanFieldErrors({
            dob: "Date of birth does not match PAN records.",
            pan_number: "Verification failed — check date of birth",
          });
        } else {
          setPanFieldErrors({ pan_number: msg });
        }
        toast.error(msg);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data?.message || "PAN verification failed. Please check your details and try again.";
      setPanFieldErrors({ pan_number: typeof detail === "string" ? detail : "Verification failed" });
      toast.error(typeof detail === "string" ? detail : "PAN verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleEmployment = async (e) => {
    e.preventDefault();
    if (!form.monthly_income) { toast.error("Enter monthly income"); return; }
    setLoading(true);
    try { await updateEmployment({ employment_type: form.employment_type, monthly_income: parseFloat(form.monthly_income) }); toast.success("Employment details saved"); setCurrentStep(2); }
    catch { toast.error("Failed to save"); } finally { setLoading(false); }
  };

  const handleUploadDocs = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      for (const [docType, file] of Object.entries(files)) {
        if (file) { const fd = new FormData(); fd.append("doc_type", docType); fd.append("file", file); await uploadDocument(fd); }
      }
      toast.success("Documents uploaded"); setCurrentStep(3);
    } catch { toast.error("Upload failed"); } finally { setLoading(false); }
  };

  const handleAadhaarUpload = async ({ aadhaar_number, aadhaar_front, aadhaar_back }) => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('aadhaar_number', aadhaar_number);
      fd.append('aadhaar_front', aadhaar_front);
      fd.append('aadhaar_back', aadhaar_back);
      await uploadAadhaar(fd);
      toast.success('Aadhaar submitted. Our team will verify it shortly.');
      setAadhaarStatus({ uploaded: true, verified: false, rejection_reason: null });
      setCurrentStep(4);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to upload Aadhaar';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleLoanDetails = async (e) => {
    e.preventDefault();
    if (!form.loan_type || !form.lender_name || !form.emi_start_date || !form.emi_end_date || !form.loan_amount || !form.emi_amount) {
      toast.error("Please fill all loan details"); return;
    }
    setLoading(true);
    try {
      await updateProfile({ loan_type: form.loan_type, lender_name: form.lender_name, emi_start_date: form.emi_start_date, emi_end_date: form.emi_end_date, loan_amount: parseFloat(form.loan_amount), emi_amount: parseFloat(form.emi_amount) });
      toast.success("Loan details saved!"); setCurrentStep(5);
    } catch { toast.error("Failed to save loan details"); } finally { setLoading(false); }
  };

  const handlePayment = async () => {
    setLoading(true);
    try {
      // Check if already subscribed
      const sub = await getSubscription();
      if (sub.data.subscription?.payment_status === "success") { 
        toast.success("Already subscribed!"); 
        navigate("/customer/dashboard"); 
        return; 
      }
      
      // Create Razorpay order
      const res = await initiatePayment({ 
        name: form.name || user?.name || "Customer", 
        email: `${user?.mobile || "user"}@rebuilt.in`, 
        phone: user?.mobile || "" 
      });
      
      if (!res.data.success) {
        toast.error("Failed to create payment order");
        setLoading(false);
        return;
      }
      
      const { order_id, amount, currency, key_id, plan_price, tier } = res.data;
      
      console.log('[Payment] Opening Razorpay checkout for subscription');
      
      // Open Razorpay Checkout
      await openRazorpayCheckout({
        order_id,
        amount,
        currency,
        key_id,
        name: 'Rebuilt',
        description: plan_price ? `EMI Support Plan - ${tier || 'Basic'} (₹${plan_price})` : 'EMI Support Plan',
        prefill_contact: user?.mobile,
        prefill_email: `${user?.mobile || "user"}@rebuilt.in`,
        onSuccess: async (response) => {
          console.log('[Payment] Razorpay payment success:', response.razorpay_payment_id);
          // v7.12 — remember the payment so the dashboard ActivationBanner can
          // keep confirming it even if verify fails / the tab is reloaded.
          markPaymentPending({
            order_id: response.razorpay_order_id,
            payment_id: response.razorpay_payment_id,
          });
          toast.success("Payment received! Activating your plan...");

          // PRIMARY: synchronously verify the signature server-side and activate
          // immediately. The dashboard banner shows a brief success state.
          try {
            const verifyRes = await verifyPayment({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            });
            if (verifyRes?.data?.success) {
              navigate("/customer/dashboard");
              return;
            }
          } catch (verifyErr) {
            console.error('[Payment] Sync verify failed — dashboard banner will confirm with Razorpay:', verifyErr);
          }

          // FALLBACK: no more spinner on this screen — the dashboard
          // ActivationBanner polls /customer/reconcile-payment (server asks
          // Razorpay directly) and flips to "Plan activated" when confirmed.
          navigate("/customer/dashboard");
        },
        onFailure: (error) => {
          console.error('[Payment] Razorpay payment failed:', error);
          toast.error(error.description || "Payment failed. Please try again.");
          setLoading(false);
        }
      });
      
    } catch (err) {
      console.error('[Payment] Error:', err);
      toast.error(err.response?.data?.detail || "Payment initiation failed"); 
      setLoading(false);
    }
  };

  const step = STEPS[currentStep];

  // Render the current step component
  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <PersonalAndPANStep
            form={form}
            setForm={setForm}
            loading={loading}
            onVerifyAndContinue={handlePersonalAndPAN}
            fieldErrors={panFieldErrors}
          />
        );
      case 1: return <EmploymentStep form={form} setForm={setForm} loading={loading} onSubmit={handleEmployment} />;
      case 2: return <DocumentsStep files={files} setFiles={setFiles} loading={loading} onSubmit={handleUploadDocs} onSkip={() => setCurrentStep(3)} />;
      case 3: {
        const token = localStorage.getItem('rebuilt_token');
        if (aadhaarMode === 'manual') {
          return (
            <AadhaarStep
              loading={loading}
              onUpload={handleAadhaarUpload}
              rejectionReason={aadhaarStatus.rejection_reason}
              onSwitchToDigilocker={() => setAadhaarMode('digilocker')}
            />
          );
        }
        return (
          <AadhaarDigiLockerStep
            userToken={token}
            backendUrl={process.env.REACT_APP_BACKEND_URL}
            onComplete={() => setCurrentStep(4)}
            onSkip={() => setAadhaarMode('manual')}
          />
        );
      }
      case 4: return <LoanDetailsStep form={form} setForm={setForm} loading={loading} onSubmit={handleLoanDetails} />;
      case 5: return <PaymentStep loading={loading} onPayment={handlePayment} onSkip={() => navigate("/customer/dashboard")} />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden" style={{background: 'linear-gradient(135deg, #0A2540 0%, #1565C0 50%, #00BCD4 100%)'}} data-testid="customer-onboarding">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 -left-20 w-96 h-96 bg-cyan-400 rounded-full blur-3xl animate-pulse-glow"></div>
        <div className="absolute bottom-20 -right-20 w-96 h-96 bg-blue-400 rounded-full blur-3xl animate-pulse-glow" style={{animationDelay: '1s'}}></div>
      </div>

      <header className="glass-dark px-4 py-4 relative z-10">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <button onClick={() => currentStep > 0 ? setCurrentStep(currentStep - 1) : navigate("/")} className="text-white/80 hover:text-white transition-colors">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <img src="/assets/logo-icon-blue.png" alt="" className="h-8 w-8 animate-float" />
          <span className="font-bold text-white tracking-tight" style={{fontFamily:'Outfit'}}>REBUILT</span>
          <span className="ml-auto text-xs text-white/70 font-medium">Step {currentStep + 1}/{STEPS.length}</span>
        </div>
      </header>

      <div className="glass-dark px-4 py-4 relative z-10">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center gap-2">
            {STEPS.map((s, i) => (
              <div key={i} className="flex-1">
                <div className={`h-2 w-full rounded-full transition-all duration-500 ${
                  i < currentStep ? 'bg-gradient-to-r from-green-400 to-green-500 shadow-glow' : 
                  i === currentStep ? 'bg-gradient-to-r from-cyan-400 to-blue-500 shadow-glow-cyan' : 
                  'bg-white/20'
                }`} />
              </div>
            ))}
          </div>
          <p className="text-sm text-white/90 mt-3 font-medium">{step.label}</p>
        </div>
      </div>

      <div className="flex-1 p-4 relative z-10">
        <div className="max-w-lg mx-auto animate-fade-in">
          <div className="glass rounded-2xl p-6 sm:p-8 shadow-xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg">
                <step.icon className="h-6 w-6 text-white" strokeWidth={2} />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 tracking-tight" style={{fontFamily:'Outfit'}}>{step.label}</h2>
            </div>
            {renderStep()}
          </div>
        </div>
      </div>
    </div>
  );
}
